import { type Location } from 'history';
import _ from 'lodash';
import { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

class PageTitleContextData {
  private titleByPathname: Record<string, string> = {};

  get(location: Location): string {
    const titleComponents: string[] = [];
    const pathTitle = this.titleByPathname[location.pathname];
    if (pathTitle) {
      titleComponents.push(pathTitle);
    } else {
      let added = 0;
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      // Keep track of last added component to avoid titles like "Agents - Agents - Notable."
      let lastAddedComponent: string | undefined;
      for (let pathComponent of location.pathname.trim().split('/').reverse()) {
        pathComponent = pathComponent.trim();
        if (
          !pathComponent.length ||
          pathComponent === lastAddedComponent ||
          uuidRegex.exec(pathComponent)
        ) {
          continue;
        }
        lastAddedComponent = pathComponent;
        titleComponents.push(_.startCase(pathComponent));
        if (++added > 1) break;
      }
    }
    titleComponents.push('Notable');
    return titleComponents.join(' | ');
  }

  set(location: Location, title: string) {
    this.titleByPathname[location.pathname] = title;
    this.updateTitle(location);
  }

  updateTitle(location: Location) {
    document.title = this.get(location);
  }
}

const PageTitleContext = createContext<PageTitleContextData>(new PageTitleContextData());

export const PageTitle: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const context = useContext(PageTitleContext);
  useEffect(() => context.updateTitle(location), [location, context]);
  return <PageTitleContext.Provider value={context}>{children}</PageTitleContext.Provider>;
};

export function usePageTitle(title: string) {
  const location = useLocation();
  const pageTitle = useContext(PageTitleContext);
  useEffect(() => pageTitle.set(location, title), [location, pageTitle, title]);
}
